import {Injectable} from '@angular/core';
import {Storage} from '@ionic/storage';
import {BehaviorSubject} from "rxjs";
import {filter, map, pairwise} from "rxjs/operators";
// import { LoggingService } from './logging.service';
import {
  AppMode,
  AuthUserInterface,
  EntityInterface,
  NoticeInterface,
  Project,
  PunchInterface,
  TimesheetPeriodInterface,
  UserPresetsInterface,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly storageKey: string = 'ngo.cam.time';
  private readonly entities$ = new BehaviorSubject<EntityInterface[]>([]);
  readonly appMode$ = new BehaviorSubject<AppMode>(AppMode.App);
  readonly isAppMode$ = new BehaviorSubject<boolean>(true);
  readonly isKioskMode$ = new BehaviorSubject<boolean>(false);
  readonly user$ = new BehaviorSubject<AuthUserInterface | null>(null);

  constructor(
    private readonly storage: Storage,
    // private readonly logger: LoggingService,
  ) {
    this.storage.create().then(() => {
      this.getUser().then((user) => {
        this.user$.next(user);
      });

      this.getEntities().then(entities => {
        this.entities$.next(entities);
      });

      this.entities$.pipe(
        pairwise(),
        filter(([prev]) => !(prev.length <= 1 && this.isKioskMode$.value)),
        map(([, curr]) => curr),
        map((entities: EntityInterface[]) => entities.length > 1 ? AppMode.Kiosk : AppMode.App),
      ).subscribe((mode: AppMode) => {
        this.appMode$.next(mode);
      });

      this.appMode$.subscribe((mode: AppMode) => {
        console.log('AppMode: ', mode);
        this.isAppMode$.next(mode === AppMode.App);
        this.isKioskMode$.next(mode === AppMode.Kiosk);
      });
    });
  }

  async getUser(): Promise<AuthUserInterface | null> {
    return (await this.storage.get(`${this.storageKey}/user`) ?? null) as AuthUserInterface | null;
  }

  async setUser(user: AuthUserInterface): Promise<void> {
    await this.storage.set(`${this.storageKey}/user`, user);
    this.user$.next(user);
  }

  async removeUser(removeData: boolean = true): Promise<void> {
    const user = await this.getUser();
    if (!!user) {
      await this.storage.remove(`${this.storageKey}/user`);
      if (removeData) {
        await this.removeUserData(user.subjectId);
      }
    }
    this.user$.next(null);
  }

  async getEntities(): Promise<EntityInterface[]> {
    return (await this.storage.get(`${this.storageKey}/entities`)) ?? [] as EntityInterface[];
  }

  async setEntities(entities: EntityInterface[]): Promise<void> {
    await this.storage.set(`${this.storageKey}/entities`, entities);
    this.entities$.next(entities);
  }

  async getPeriods(year: number): Promise<TimesheetPeriodInterface[]> {
    const years = [year - 1, year, year + 1];
    const periodsGroupedByYear = await Promise.all(
      years.map(
        (year) => this.storage.get(`${this.storageKey}/periods:${year}`) as Promise<TimesheetPeriodInterface[] | null>,
      ),
    );
    return periodsGroupedByYear.reduce((acc, current) => acc.concat(current ?? []), []);
  }

  async setPeriods(year: number, periods: TimesheetPeriodInterface[]): Promise<void> {
    await this.storage.set(`${this.storageKey}/periods:${year}`, periods);
  }

  async removePeriods(year: number): Promise<void> {
    await this.storage.remove(`${this.storageKey}/periods:${year}`);
  }

  async getProjects(): Promise<Project[]> {
    return (await this.storage.get(`${this.storageKey}/projects`)) as Project[];
  }

  async setProjects(projects: Project[]): Promise<void> {
    await this.storage.set(`${this.storageKey}/projects`, projects);
  }

  async getPresets(subjectId: string): Promise<UserPresetsInterface> {
    const presets = (await this.storage.get(`${this.storageKey}/presets/${subjectId}`)) as UserPresetsInterface;
    return this.reshapePresets(presets);
  }

  async setPresets(subjectId: string, presets: UserPresetsInterface): Promise<void> {
    await this.storage.set(`${this.storageKey}/presets/${subjectId}`, presets);
  }

  // ** 3/2026 - ensure presets are the correct shape - can be removed eventually
  reshapePresets(presets: UserPresetsInterface): UserPresetsInterface {
    if (presets) {
      const {subjectId, userPin, ...rest} = presets;
      presets = rest;
      presets.data = (presets.data ?? []).map((data) => {
        const {project, ...rest} = data;
        return ({...rest, projectId: data.projectId ?? project});
      });
    }
    return presets;
  }

  async getPunches(subjectId: string): Promise<{ periodNumber: number; closed: boolean; punches: PunchInterface[] }[]> {
    return await this.storage.get(`${this.storageKey}/punches/${subjectId}`) as {
      periodNumber: number;
      closed: boolean;
      punches: PunchInterface[]
    }[];
  }

  async setPunches(
    subjectId: string,
    data: { periodNumber: number; closed: boolean; punches: PunchInterface[] }[],
  ): Promise<void> {
    await this.storage.set(`${this.storageKey}/punches/${subjectId}`, data);
  }

  async getNotices(subjectId: string): Promise<NoticeInterface[]> {
    return (await this.storage.get(`${this.storageKey}/notices/${subjectId}`)) ?? ([] as NoticeInterface[]);
  }

  async setNotices(subjectId: string, notices: NoticeInterface[]): Promise<void> {
    await this.storage.set(`${this.storageKey}/notices/${subjectId}`, notices);
  }

  async getLastModified(idx: string): Promise<string> {
    const data = (await this.storage.get(`${this.storageKey}/lastModified`)) as { [index: string]: string };
    return data ? data[idx] : null;
  }

  async setLastModified(idx: string, dateModified: string): Promise<void> {
    const data = (await this.storage.get(`${this.storageKey}/lastModified`)) ?? {};
    // if (data.periods) delete data.periods; // clean up legacy data
    data[idx] = dateModified;
    await this.storage.set(`${this.storageKey}/lastModified`, data);
  }

  async removeNotices(subjectId: string): Promise<void> {
    await this.storage.remove(`${this.storageKey}/notices/${subjectId}`);
  }

  async removePresets(subjectId: string): Promise<void> {
    await this.storage.remove(`${this.storageKey}/presets/${subjectId}`);
  }

  async removePunches(subjectId: string): Promise<void> {
    await this.storage.remove(`${this.storageKey}/punches/${subjectId}`);
  }

  async removeUserData(subjectId: string): Promise<void> {
    await this.removeNotices(subjectId);
    await this.removePresets(subjectId);
    await this.removePunches(subjectId);
  }

  async removeStorage(): Promise<void> {
    await this.storage.forEach((value, key, index) => {
      if (key.startsWith(this.storageKey)) {
        this.storage.remove(key).then();
      }
    });
  }
}
